/**
 * BrandStylePanel — Brand Styler inheritance in Admin.
 * Client (brand group): pick the brand profile every branch inherits.
 * Branch: inherit the brand's styling (default) or override with its own profile.
 * The effective profile restyles the Reels masters at render time.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Palette, Loader2, Check, Link2, Unlink, Building2, ArrowRight, X } from "lucide-react";
import "./BrandStylePanel.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/brand-style`;

export default function BrandStylePanel({ scope, target, onClose, onOpenStyler }) {
  const [state, setState] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const path = `${scope === "client" ? "clients" : "branches"}/${target.id}`;

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([axios.get(`${API}/${path}`), axios.get(`${API}/profiles`)]);
      setState(s.data);
      setProfiles(p.data.profiles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load brand styling");
    }
  }, [path]);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/${path}`, body);
      setState(r.data);
      toast.success("Brand styling saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (!state) return <div className="bsp bsp-loading" data-testid="brand-style-loading"><Loader2 size={16} className="animate-spin" /> Loading brand styling…</div>;

  return (
    <section className="bsp" data-testid={`brand-style-panel-${scope}`}>
      <header className="bsp-head">
        <div className="bsp-head-icon"><Palette size={18} /></div>
        <div className="bsp-head-text">
          <h3>Brand Styler · {target.name}</h3>
          <p>{scope === "client"
            ? "The brand profile every branch under this brand renders its Just Listed / Price Reduction / Sold reels with — unless a branch overrides it."
            : "Reels render with the brand's colours, fonts and logo unless this branch is switched to its own profile."}</p>
        </div>
        {onClose && <button className="bsp-close" onClick={onClose} aria-label="Close" data-testid="brand-style-close"><X size={16} /></button>}
      </header>

      {scope === "client" ? (
        <>
          <div className="bsp-row">
            <ProfilePicker value={state.brand_profile_id} profiles={profiles} disabled={saving} onChange={(id) => save({ brand_profile_id: id })} testid="brand-style-client-picker" />
            {saving && <Loader2 size={14} className="animate-spin" />}
          </div>
          <ProfileCard profile={state.profile} label="Brand default" empty="No brand profile attached yet — pick one above, or create it in Brand Styler." />
          {state.branches?.length > 0 && (
            <div className="bsp-branches" data-testid="brand-style-branch-list">
              <span className="bsp-sub">Branches</span>
              <ul>
                {state.branches.map((b) => (
                  <li key={b.id}>
                    <Building2 size={13} /> <span className="bsp-branch-name">{b.name}</span>
                    {b.mode === "override" ? <span className="bsp-tag is-override"><Unlink size={11} /> Override · {b.brand_name || "no profile"}</span> : <span className="bsp-tag"><Link2 size={11} /> Inherits</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="bsp-modes" role="radiogroup">
            <button type="button" role="radio" aria-checked={state.mode === "inherit"} className={`bsp-mode ${state.mode === "inherit" ? "is-on" : ""}`} disabled={saving} onClick={() => save({ mode: "inherit", brand_profile_id: state.brand_profile_id })} data-testid="brand-style-mode-inherit">
              <Link2 size={15} /><strong>Inherit brand styling</strong><small>{state.client_name ? `Follows ${state.client_name}` : "Follows the brand group"}</small>
            </button>
            <button type="button" role="radio" aria-checked={state.mode === "override"} className={`bsp-mode ${state.mode === "override" ? "is-on" : ""}`} disabled={saving} onClick={() => save({ mode: "override", brand_profile_id: state.brand_profile_id })} data-testid="brand-style-mode-override">
              <Unlink size={15} /><strong>Override for this branch</strong><small>Its own colours, fonts and logo</small>
            </button>
          </div>
          {state.mode === "override" ? (
            <>
              <div className="bsp-row">
                <ProfilePicker value={state.brand_profile_id} profiles={profiles} disabled={saving} onChange={(id) => save({ mode: "override", brand_profile_id: id })} testid="brand-style-branch-picker" />
                {saving && <Loader2 size={14} className="animate-spin" />}
              </div>
              <ProfileCard profile={state.own_profile} label="This branch renders with" empty="Pick the branch's own profile above." />
            </>
          ) : (
            <ProfileCard profile={state.inherited_profile} label={`Inherited from ${state.client_name || "the brand"}`} empty={`${state.client_name || "The brand"} has no brand profile yet — set it on the client page, or override here.`} />
          )}
        </>
      )}

      <footer className="bsp-foot">
        <span className="bsp-effective" data-testid="brand-style-effective">
          {scope === "client"
            ? (state.profile ? <><Check size={13} /> Branches render as <strong>{state.profile.name}</strong> unless overridden</> : "Reels use the plain Reels Styler master until a profile is attached")
            : (state.effective ? <><Check size={13} /> Renders as <strong>{state.effective.name}</strong> ({state.source === "branch" ? "branch override" : "inherited"})</> : "Renders with the plain Reels Styler master")}
        </span>
        {onOpenStyler && <button className="bsp-link" onClick={onOpenStyler} data-testid="brand-style-open-styler">Create or edit profiles in Brand Styler <ArrowRight size={13} /></button>}
      </footer>
    </section>
  );
}

function ProfilePicker({ value, profiles, onChange, disabled, testid }) {
  return (
    <label className="bsp-picker">
      <span>Brand profile</span>
      <select value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} data-testid={testid}>
        <option value="">— none —</option>
        {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </label>
  );
}

function ProfileCard({ profile, label, empty }) {
  if (!profile) return <div className="bsp-card bsp-card-empty" data-testid="brand-style-profile-empty">{empty}</div>;
  return (
    <div className="bsp-card" data-testid="brand-style-profile-card">
      <div className="bsp-logo" style={{ background: profile.neutral || "#fff" }}>
        {profile.logo_url ? <img src={profile.logo_url} alt={profile.name} /> : <span style={{ color: profile.primary }}>{profile.name?.[0]}</span>}
      </div>
      <div className="bsp-card-body">
        <span className="bsp-sub">{label}</span>
        <h4>{profile.name}</h4>
        <div className="bsp-swatches">
          {["primary", "accent", "secondary", "neutral"].map((r) => <i key={r} style={{ background: profile[r] }} title={`${r} ${profile[r]}`} />)}
          <small>{profile.hero_font} · {profile.support_font}</small>
        </div>
      </div>
    </div>
  );
}
